import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { io, type Socket } from 'socket.io-client'
import { apiClient } from '../client'
import { useAuthStore } from '../../store/authStore'
import ENV from '../../config/env'
import { invalidateConnectionSurfaces } from './useConnections'

// Mirrors apps/mobile/app/(app)/chat/*.tsx against the same HTTP
// endpoints, plus a Socket.io subscription for instant inbound that
// mobile doesn't have today. Polling (refetchInterval) still kicks in
// at 5–15s as a fallback if the socket connection is lost.

export interface ChatListItem {
  userId: number
  firstName: string
  lastName: string
  profilePhoto?: string | null
  lastMessage?: string | null
  lastMessageTime?: string | null
  unreadCount: number
}

export function useChatList() {
  return useQuery({
    queryKey: ['chat-list'],
    queryFn: async () => {
      const res = await apiClient.get<ChatListItem[]>('/api/chat/chat-list')
      return res.data
    },
    refetchInterval: 15000,
  })
}

export interface ChatMessage {
  id: number
  senderId: number
  receiverId: number
  content: string
  createdAt: string
  readAt?: string | null
}

export function useChatMessages(recipientId: number | null) {
  return useQuery({
    queryKey: ['messages', recipientId],
    queryFn: async () => {
      if (!recipientId) return []
      const res = await apiClient.get<ChatMessage[]>(`/api/chat/messages/${recipientId}`)
      // Mark conversation as read on every fetch — same as mobile.
      apiClient.post('/api/chat/mark-as-read', { senderId: recipientId }).catch(() => {})
      return res.data
    },
    enabled: !!recipientId,
    refetchInterval: 5000,
  })
}

export function useSendMessage(recipientId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (content: string) => {
      const res = await apiClient.post('/api/chat/send', {
        receiverId: recipientId,
        content,
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messages', recipientId] })
      qc.invalidateQueries({ queryKey: ['chat-list'] })
    },
  })
}

// Socket.io subscription — open one connection per session for the
// viewer's user-room (the backend joins user:${userId} automatically
// based on the `userId` connection query). Invalidates relevant
// React Query caches when relevant events arrive.
export function useChatSocket() {
  const myId = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    if (!myId) return
    const socket = io(ENV.SOCKET_URL, {
      query: { userId: String(myId) },
      transports: ['websocket', 'polling'],
      withCredentials: false,
    })
    socketRef.current = socket

    socket.on('new_message', (data: { senderId: number; receiverId: number; content: string; messageId: number }) => {
      // Inbound message arrived — refresh the open thread, the chat
      // list, AND the sidebar unread badge. #invalidation-key-fixes:
      // the badge reads ['chat-list-badge', userId] (deliberately
      // namespaced apart from ['chat-list']), so without the explicit
      // line a live message updated the thread but the sidebar count
      // lagged until the next 30s poll.
      qc.invalidateQueries({ queryKey: ['messages', data.senderId] })
      qc.invalidateQueries({ queryKey: ['chat-list'] })
      qc.invalidateQueries({ queryKey: ['chat-list-badge', myId] })
    })

    socket.on('messages_read', () => {
      qc.invalidateQueries({ queryKey: ['chat-list'] })
      qc.invalidateQueries({ queryKey: ['chat-list-badge', myId] })
    })

    // #realtime-notification-push — server-side connection events
    // (request sent / accepted / countered) nudge every connection-
    // derived surface. The payload is a hint; we just refetch. This is
    // what makes requests and badges appear without the 30s poll —
    // mounted app-wide via AppShell.
    socket.on('notification_push', () => {
      invalidateConnectionSurfaces(qc, myId)
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [myId, qc])

  return socketRef
}
